import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import ContentHeader from "../../../widgets/contentHeader/ContentHeader";
import { getItemWebStorage } from "../../../features/webStorageSaver";
import MapWidget from "../../../widgets/map/mapWidget/MapWidget";
import { MapOverlayWidget } from "../../../widgets/map/mapAddons/mapOverlayWidget";
import { MapTooltip } from "../../../widgets/map/mapAddons/mapTooltip";
import { getAllObjectsLayer, getBasicObjectTypeLayer } from "../../../widgets/map/mapFeatures/layers";
import { showMapTooltip } from "../../../widgets/map/mapFeatures/mapFunctions/showMapTooltip/showMapTooltip";
import {
    getConfigMapDefinitionById,
    getConfigMapFilterRouteUrl
} from "../../../features/configPages/configMapDefinitions";
import { useAppSelector } from "../../../stateManagement/hooks/redux";
import { setMapTooltipIsVisible } from "../../../stateManagement/reducers/MapTooltipSlice";
import { LastYearsMapFilter, buildYearsEnv, getLastYearsCqlFilter } from "../../lastYearsMapFilter";

type Props = {
    definitionId: string;
};

export function ConfigDefinedMapPage({ definitionId }: Props) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isSmallWindow, setIsSmallWindow] = useState(true);
    const sideBarState = useAppSelector(state => state.sidebar.sidebarIsShown)
    const yearFilter = useAppSelector(state => state.mapFilters?.lastYearsMapFilters)?.[definitionId] ?? {}

    const currentConfig = getItemWebStorage("currentConfig") ?? {};
    const { wmsServer, wmsVersion, workspace } = currentConfig as any;
    const mapDefinition = getConfigMapDefinitionById(currentConfig, definitionId);
    const layerName = mapDefinition?.source?.kind === "wmsLayer"
        ? mapDefinition?.source?.layerName
        : undefined;
    const lastYearsFilter = mapDefinition?.lastYearsFilter;
    const tooltipType = mapDefinition?.tooltip?.type;
    const roadNetworkEnabled = Boolean(mapDefinition?.roadNetwork?.enabled);
    const filterRoute = getConfigMapFilterRouteUrl(mapDefinition?.url);
    const numberOfYears = lastYearsFilter?.numberOfYears ?? 5;
    const effectiveYearFilter = Object.keys(yearFilter).length > 0
        ? yearFilter
        : { [new Date().getFullYear()]: true };
    const layerFilter = lastYearsFilter
        ? getLastYearsCqlFilter(effectiveYearFilter, lastYearsFilter?.fieldName ?? "EventYear")
        : undefined;
    const layerEnv = lastYearsFilter ? buildYearsEnv(numberOfYears) : undefined;

    const layer = useMemo(() => {
        if (!layerName || !wmsServer || !wmsVersion || !workspace) return null;
        return getBasicObjectTypeLayer(layerName, wmsServer, wmsVersion, workspace, layerFilter, undefined, layerEnv);
    }, [layerEnv, layerFilter, layerName, wmsServer, wmsVersion, workspace]);

    useEffect(() => {
        if (!mapDefinition || !layerName || !layer) {
            navigate("/app/error");
        }
    }, [layer, layerName, mapDefinition, navigate]);

    useEffect(() => {
        const contentHeaderWidth: any = document.querySelector('.content-header-div')?.getBoundingClientRect().width;
        setIsSmallWindow(contentHeaderWidth < 440);
    }, [sideBarState, navigate])

    useEffect(() => {
        const contentHeaderWidth: any = document.querySelector('.content-header-div')?.getBoundingClientRect().width;
        const hasSeenYearFilter = getItemWebStorage("lastYearsMapFilters")?.[definitionId]
        if (!lastYearsFilter || !filterRoute) return;
        if (!hasSeenYearFilter && contentHeaderWidth < 440) {
            navigate(filterRoute)
        }
    }, [definitionId, filterRoute, lastYearsFilter, navigate])

    const additionalLayers = useMemo(() => layer ? [layer] : [], [layer]);
    const permanentLayers = useMemo(() => roadNetworkEnabled ? [getAllObjectsLayer()] : [], [roadNetworkEnabled]);
    const mapEventHandlers = useMemo(() => {
        if (!layer || !tooltipType) return [];
        return [
            {
                name: "mapTooltip",
                func: (map: any) => {
                    const pointerMoveFunc = (event: any) => showMapTooltip(event, tooltipType, layer, map);
                    map?.on("pointermove", pointerMoveFunc);
                    return {
                        off: () => {
                            map?.un("pointermove", pointerMoveFunc);
                            dispatch(setMapTooltipIsVisible(false));
                        }
                    };
                }
            }
        ];
    }, [dispatch, layer, tooltipType]);

    return (
        <div className="config-defined-map-page">
            <ContentHeader
                title={mapDefinition?.title}
                objectTypeName={definitionId}
                type="map"
                option={lastYearsFilter ? "configLastYearsFilter" : undefined}
                filterRoute={lastYearsFilter ? filterRoute : undefined}
                filterButtonTitle={lastYearsFilter?.filterButtonTitle}
            />
            {!isSmallWindow && lastYearsFilter &&
                <LastYearsMapFilter
                    filterType="header"
                    numberOfYears={numberOfYears}
                    objectType={definitionId}
                    pagePrompt={lastYearsFilter?.pagePrompt}
                />
            }
            <div className={`map-widget-with-toast config-defined-map-page-map${!isSmallWindow && lastYearsFilter ? ' with-last-years-filter-row' : ''}`}>
                {tooltipType && <MapTooltip type={tooltipType} />}
                <MapWidget additionalLayers={additionalLayers} mapEventHandlers={mapEventHandlers} permanentLayers={permanentLayers} />
                <MapOverlayWidget />
            </div>
        </div>
    );
}
